import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { RouterModule } from '@angular/router';
import { KeepaGraphComponent } from '../../parts/keepa-graph/keepa-graph.component';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DateService } from 'src/app/services/date.service';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import {
  UserAmazonNotificationsGQL,
  UserAmazonNotificationsQuery,
  UpsertUserAmazonNotificationGQL,
  RemoveUserAmazonNotificationGQL,
} from 'src/gql/generated';
import { gqlErrors } from 'src/app/utils/gql-errors';

@Component({
  selector: 'app-amazon-notification-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    PanelModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    DatePickerModule,
    DialogModule,
    Textarea,
    RouterModule,
    KeepaGraphComponent,
  ],
})
export class AmazonNotificationListComponent implements OnInit {
  notifications: UserAmazonNotificationsQuery['userAmazonNotifications'] = [];
  loading = false;
  createdAtFrom: Date;

  // メモ編集用
  showMemoDialog = false;
  currentNotification: UserAmazonNotificationsQuery['userAmazonNotifications'][0] | null = null;
  memoText = '';

  // 一括選択用
  selectedNotifications: UserAmazonNotificationsQuery['userAmazonNotifications'] = [];

  constructor(
    private router: Router,
    private routingService: RoutingService,
    private subtitleService: SubtitleService,
    private userService: UserService,
    private dateService: DateService,
    private messageService: MessageService,
    private userAmazonNotificationsGQL: UserAmazonNotificationsGQL,
    private upsertUserAmazonNotificationGQL: UpsertUserAmazonNotificationGQL,
    private removeUserAmazonNotificationGQL: RemoveUserAmazonNotificationGQL,
  ) {
    this.routingService.loginRequired();
    this.userService.amazonNotifyRequired();
    this.subtitleService.setSubTitle('Amazon通知一覧');

    // デフォルトで30日前の日付を設定
    this.createdAtFrom = new Date();
    this.createdAtFrom.setDate(this.createdAtFrom.getDate() - 30);
  }

  async ngOnInit() {
    await this.loadNotifications();
  }

  async loadNotifications() {
    this.loading = true;
    try {
      const createdAtFromStr = this.dateService.formatDate(this.createdAtFrom, 'YYYY-MM-DD');
      console.log('Fetching notifications with createdAtFrom:', createdAtFromStr);
      const result = await firstValueFrom(
        this.userAmazonNotificationsGQL.fetch({ variables: { createdAtFrom: createdAtFromStr } }),
      );
      console.log('GraphQL result:', result);

      // GraphQLエラーをチェック
      if (gqlErrors(result)?.length > 0) {
        const error = gqlErrors(result)[0];
        let errorMessage = error.message || 'データの取得に失敗しました';

        // バリデーションエラーの場合、より詳細なメッセージを表示
        if (error.extensions?.code === 'W0001') {
          errorMessage = 'バリデーションエラーが発生しました。入力内容を確認してください。';
        }

        this.messageService.setFlashMessageWithClear('error', 'エラー', errorMessage);
        console.error('GraphQL errors:', gqlErrors(result));
        this.notifications = [];
        return;
      }

      this.notifications = result.data?.userAmazonNotifications || [];
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', 'データの取得に失敗しました');
      console.error('Error loading notifications:', error);
      this.notifications = [];
    } finally {
      this.loading = false;
    }
  }

  async onDateChange() {
    await this.loadNotifications();
  }

  navigateToRegister() {
    this.router.navigate(['/amazon-notification/register']);
  }

  navigateToMain() {
    this.router.navigate(['/amazon-notification']);
  }

  formatDate(dateString: string): string {
    return this.dateService.formatDate(new Date(dateString), 'YYYY/MM/DD HH:mm');
  }

  openMemoDialog(notification: UserAmazonNotificationsQuery['userAmazonNotifications'][0]) {
    this.currentNotification = notification;
    this.memoText = notification.memo || '';
    this.showMemoDialog = true;
  }

  async saveMemo() {
    if (!this.currentNotification) return;

    try {
      const result = await firstValueFrom(
        this.upsertUserAmazonNotificationGQL.mutate({
          variables: {
            asin: this.currentNotification.amazonProduct.asin,
            memo: this.memoText || null,
          },
        }),
      );
      console.log('Memo save result:', result);

      // GraphQLエラーをチェック
      if (gqlErrors(result)?.length > 0) {
        const error = gqlErrors(result)[0];
        let errorMessage = error.message || 'メモの保存に失敗しました';

        if (error.extensions?.code === 'W0001') {
          errorMessage = 'バリデーションエラーが発生しました。入力内容を確認してください。';
        }

        this.messageService.setFlashMessageWithClear('error', 'エラー', errorMessage);
        console.error('GraphQL errors:', gqlErrors(result));
        return;
      }

      if (result.data?.upsertUserAmazonNotification === true) {
        // 成功時の処理 - 配列を更新して読み取り専用プロパティの問題を回避
        this.notifications = this.notifications.map((notification) => {
          if (notification.id === this.currentNotification?.id) {
            return {
              ...notification,
              memo: this.memoText,
            };
          }
          return notification;
        });

        this.messageService.setFlashMessageWithClear('success', '成功', 'メモを保存しました');
        this.closeMemoDialog();
      } else {
        console.log('Unexpected result data:', result.data);
        this.messageService.setFlashMessageWithClear('error', 'エラー', 'メモの保存に失敗しました');
      }
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', 'メモの保存に失敗しました');
      console.error('Error saving memo:', error);
    }
  }

  closeMemoDialog() {
    this.showMemoDialog = false;
    this.currentNotification = null;
    this.memoText = '';
  }

  // 単一削除（確認なし）
  async deleteNotification(notification: UserAmazonNotificationsQuery['userAmazonNotifications'][0]) {
    try {
      const result = await firstValueFrom(
        this.removeUserAmazonNotificationGQL.mutate({
          variables: {
            asin: notification.amazonProduct.asin,
          },
        }),
      );

      if (gqlErrors(result)?.length > 0) {
        const error = gqlErrors(result)[0];
        let errorMessage = error.message || '削除に失敗しました';
        if (error.extensions?.code === 'W0001') {
          errorMessage = 'バリデーションエラーが発生しました。';
        }
        this.messageService.setFlashMessageWithClear('error', 'エラー', errorMessage);
        return;
      }

      if (result.data?.removeUserAmazonNotification === true) {
        this.notifications = this.notifications.filter((n) => n.id !== notification.id);
        this.selectedNotifications = this.selectedNotifications.filter((n) => n.id !== notification.id);
        this.messageService.setFlashMessageWithClear('success', '成功', '削除しました');
      } else {
        this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
      }
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
      console.error('Error deleting notification:', error);
    }
  }

  // 一括削除（逐次処理）
  async deleteSelected() {
    if (this.selectedNotifications.length === 0) return;

    this.loading = true;
    let successCount = 0;
    let failCount = 0;
    const toDelete = [...this.selectedNotifications];

    for (const notification of toDelete) {
      try {
        const result = await firstValueFrom(
          this.removeUserAmazonNotificationGQL.mutate({
            variables: {
              asin: notification.amazonProduct.asin,
            },
          }),
        );

        if (result.data?.removeUserAmazonNotification === true) {
          successCount++;
          this.notifications = this.notifications.filter((n) => n.id !== notification.id);
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
        console.error('Error deleting notification:', error);
      }
    }

    this.selectedNotifications = [];
    this.loading = false;

    if (failCount === 0) {
      this.messageService.setFlashMessageWithClear('success', '成功', `${successCount}件削除しました`);
    } else if (successCount > 0) {
      this.messageService.setFlashMessageWithClear('warn', '警告', `${successCount}件削除、${failCount}件失敗`);
    } else {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
    }
  }
}
